// src/components/CreateJob.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  Timestamp,
  query,
  where,
  onSnapshot,
  doc,
  runTransaction,
  setDoc,
  orderBy,
} from "firebase/firestore";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Divider,
  Alert,
  Collapse,
  InputAdornment,
  Chip,
  Avatar,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Autocomplete,
  FormHelperText,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DescriptionIcon from "@mui/icons-material/Description";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import NotesIcon from "@mui/icons-material/Notes";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClearIcon from "@mui/icons-material/Clear";

import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { useNavigation } from "../contexts/NavigationContext";

function CreateJob() {
  const { navigateToAdminPage } = useNavigation();

  // Client State
  const [selectedClient, setSelectedClient] = useState("");
  const [clients, setClients] = useState([]);

  // Job State
  const [siteAddress, setSiteAddress] = useState("");
  const [siteContact, setSiteContact] = useState("");
  const [vehicleReg, setVehicleReg] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [jobNotes, setJobNotes] = useState("");
  const [endCustomerName, setEndCustomerName] = useState("");
  const [endCustomerAddress, setEndCustomerAddress] = useState("");
  const [endCustomerContact, setEndCustomerContact] = useState("");
  const [endCustomerPhone, setEndCustomerPhone] = useState("");
  const [showEndCustomer, setShowEndCustomer] = useState(false);
  // Schedule
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("09:00");

  // Engineer & Template State
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [lastCreatedJobNumber, setLastCreatedJobNumber] = useState(null);

  // Fetch engineers, templates, clients
  useEffect(() => {
    let engineersLoaded = false;
    let templatesLoaded = false;
    let clientsLoaded = false;

    const checkAllLoaded = () => {
      if (engineersLoaded && templatesLoaded && clientsLoaded) {
        setLoadingData(false);
      }
    };

    const engineersQuery = query(
      collection(db, "users"),
      where("role", "==", "engineer"),
      orderBy("firstName")
    );
    const unsubEngineers = onSnapshot(engineersQuery, (querySnapshot) => {
      const engineerList = [];
      querySnapshot.forEach((doc) => {
        engineerList.push({ id: doc.id, ...doc.data() });
      });
      setEngineers(engineerList);
      if (engineerList.length > 0 && !selectedEngineer) {
        setSelectedEngineer(engineerList[0].id);
      }
      engineersLoaded = true;
      checkAllLoaded();
    });

    const templatesQuery = query(collection(db, "jobSheetTemplates"), orderBy("name"));
    const unsubTemplates = onSnapshot(templatesQuery, (querySnapshot) => {
      const templatesList = [];
      querySnapshot.forEach((doc) => {
        templatesList.push({ id: doc.id, name: doc.data().name });
      });
      setTemplates(templatesList);
      if (templatesList.length > 0 && !selectedTemplate) {
        setSelectedTemplate(templatesList[0].id);
      }
      templatesLoaded = true;
      checkAllLoaded();
    });

    const clientsQuery = query(collection(db, "customers"), orderBy("name"));
    const unsubClients = onSnapshot(clientsQuery, (querySnapshot) => {
      const clientsList = [];
      querySnapshot.forEach((doc) => {
        clientsList.push({ id: doc.id, ...doc.data() });
      });
      setClients(clientsList);
      if (clientsList.length > 0 && !selectedClient) {
        setSelectedClient(clientsList[0].id);
      }
      clientsLoaded = true;
      checkAllLoaded();
    });

    return () => {
      unsubEngineers();
      unsubTemplates();
      unsubClients();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill from selected client
  useEffect(() => {
    if (selectedClient && clients.length > 0) {
      const client = clients.find((c) => c.id === selectedClient);
      if (client) {
        setSiteAddress(client.address || "");
        setSiteContact(client.contactPhone || client.contactEmail || "");
      }
    }
  }, [selectedClient, clients]);

  // Get selected data
  const selectedClientData = useMemo(() => 
    clients.find((c) => c.id === selectedClient), 
    [clients, selectedClient]
  );

  const selectedEngineerData = useMemo(() => 
    engineers.find((e) => e.id === selectedEngineer), 
    [engineers, selectedEngineer]
  );

  const selectedTemplateData = useMemo(() => 
    templates.find((t) => t.id === selectedTemplate), 
    [templates, selectedTemplate]
  );

  // Set default date to today
  useEffect(() => {
    if (!scheduledDate) {
      const today = new Date().toISOString().split('T')[0];
      setScheduledDate(today);
    }
  }, [scheduledDate]);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!selectedClient) newErrors.client = "Please select a client";
    if (!selectedEngineer) newErrors.engineer = "Please select an engineer";
    if (!selectedTemplate) newErrors.template = "Please select a template";
    if (!scheduledDate) newErrors.scheduledDate = "Please select a date";
    if (!scheduledTime) newErrors.scheduledTime = "Please select a time";
    if (!siteAddress.trim()) newErrors.siteAddress = "Site address is required";
    if (!siteContact.trim()) newErrors.siteContact = "Site contact is required";
    if (!vehicleReg.trim()) newErrors.vehicleReg = "Vehicle registration is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage({
        type: "error",
        text: "Please fix all errors before submitting",
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const templateName = selectedTemplateData?.name || "Unknown Template";
    const clientName = selectedClientData?.name || "Unknown Client";
    const engineerName = selectedEngineerData
      ? `${selectedEngineerData.firstName || ""} ${selectedEngineerData.lastName || ""}`.trim() ||
        selectedEngineerData.email
      : "Unknown Engineer";

    try {
      const counterDocRef = doc(db, "counters", "jobCounter");

      const newJobNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterDocRef);
        if (!counterDoc.exists()) {
          transaction.set(counterDocRef, { currentValue: 1000 });
          return 1000;
        }
        const newJobNum = counterDoc.data().currentValue + 1;
        transaction.update(counterDocRef, { currentValue: newJobNum });
        return newJobNum;
      });

      const [year, month, day] = scheduledDate.split("-");
      const [hours, minutes] = scheduledTime.split(":");
      const combinedDateTime = new Date(year, month - 1, day, hours, minutes);

      const newJob = {
        jobReferenceNumber: newJobNumber,
        status: "Pending",
        createdAt: Timestamp.now(),

        clientId: selectedClient,
        customerName: clientName,
        siteAddress: siteAddress.trim(),
        siteContact: siteContact.trim(),
        endCustomerName: endCustomerName.trim() || null,
        endCustomerAddress: endCustomerAddress.trim() || null,
        endCustomerContact: endCustomerContact.trim() || null,
        endCustomerPhone: endCustomerPhone.trim() || null,
        vehicleReg: vehicleReg.trim(),
        vehicleMake: vehicleMake.trim() || null,
        jobNotes: jobNotes.trim() || null,
        scheduledDateTime: Timestamp.fromDate(combinedDateTime),

        engineerId: selectedEngineer,
        engineerName,
        jobTemplateId: selectedTemplate,
        jobTemplateName: templateName,

        assignedEngineerUid: selectedEngineer,
        assignedEngineerEmail: selectedEngineerData?.email || null,
        assignedEngineerName: engineerName,
      };

      const newJobRef = doc(collection(db, "jobs"));
      await setDoc(newJobRef, newJob);

      setMessage({
        type: "success",
        text: `✓ Job #${newJobNumber} created successfully and assigned to ${engineerName}!`,
      });

      setLastCreatedJobNumber(newJobNumber);

      // Clear form fields
      setSiteAddress("");
      setSiteContact("");
      setVehicleReg("");
      setVehicleMake("");
      setJobNotes("");
      setScheduledDate(new Date().toISOString().split('T')[0]);
      setScheduledTime("09:00");
      setErrors({});

      // Auto-hide success after 5 seconds
      setTimeout(() => setMessage(null), 5000);

    } catch (err) {
      console.error("Error creating job:", err);
      setMessage({ 
        type: "error", 
        text: `Failed to create job: ${err.message}` 
      });
    }
    setIsLoading(false);
  };

  const handleClearForm = () => {
    setSiteAddress("");
    setSiteContact("");
    setVehicleReg("");
    setVehicleMake("");
    setJobNotes("");
    setScheduledDate(new Date().toISOString().split('T')[0]);
    setScheduledTime("09:00");
    setErrors({});
    setMessage(null);
  };

  const handleDuplicateLast = () => {
    // Keep selections but reset other fields for quick repeat jobs
    setMessage({
      type: "info",
      text: "Form ready for another job with the same client, engineer, and template",
    });
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
            <AddCircleIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Create New Job
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign a job to an engineer and link it to a client
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(15,23,42,0.95))",
            border: "1px solid rgba(59,130,246,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {clients.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clients Available
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(34,197,94,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "success.main", width: 40, height: 40 }}>
                  <EngineeringIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {engineers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Engineers Available
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(139,92,246,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {templates.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Job Templates
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Warnings */}
      {clients.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button size="small" onClick={() => navigateToAdminPage('clients')}>
            Add Client
          </Button>
        }>
          No clients found. You need at least one client to create a job.
        </Alert>
      )}
      {engineers.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button size="small" onClick={() => navigateToAdminPage('engineers')}>
            Add Engineer
          </Button>
        }>
          No engineers found. You need at least one engineer to assign jobs.
        </Alert>
      )}
      {templates.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button size="small" onClick={() => navigateToAdminPage('designer')}>
            Create Template
          </Button>
        }>
          No job templates found. Create a template first.
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid rgba(148,163,184,0.2)",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {/* Section: Schedule */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CalendarTodayIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Schedule
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => {
                    setScheduledDate(e.target.value);
                    if (errors.scheduledDate) setErrors({ ...errors, scheduledDate: null });
                  }}
                  label="Scheduled Date"
                  fullWidth
                  required
                  error={!!errors.scheduledDate}
                  helperText={errors.scheduledDate}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => {
                    setScheduledTime(e.target.value);
                    if (errors.scheduledTime) setErrors({ ...errors, scheduledTime: null });
                  }}
                  label="Arrival Time"
                  fullWidth
                  required
                  error={!!errors.scheduledTime}
                  helperText={errors.scheduledTime}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Section: Client & Assignment */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Client & Assignment
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!errors.client}>
                  <InputLabel>Select Client</InputLabel>
                  <Select
                    value={selectedClient}
                    label="Select Client"
                    onChange={(e) => {
                      setSelectedClient(e.target.value);
                      if (errors.client) setErrors({ ...errors, client: null });
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <BusinessIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.client && <FormHelperText>{errors.client}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!errors.engineer}>
                  <InputLabel>Assign to Engineer</InputLabel>
                  <Select
                    value={selectedEngineer}
                    label="Assign to Engineer"
                    onChange={(e) => {
                      setSelectedEngineer(e.target.value);
                      if (errors.engineer) setErrors({ ...errors, engineer: null });
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <EngineeringIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {engineers.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {`${e.firstName || ""} ${e.lastName || ""}`.trim() || e.email}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.engineer && <FormHelperText>{errors.engineer}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!errors.template}>
                  <InputLabel>Job Sheet Template</InputLabel>
                  <Select
                    value={selectedTemplate}
                    label="Job Sheet Template"
                    onChange={(e) => {
                      setSelectedTemplate(e.target.value);
                      if (errors.template) setErrors({ ...errors, template: null });
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <DescriptionIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.template && <FormHelperText>{errors.template}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>

            {/* Show selected info */}
            {(selectedClientData || selectedEngineerData || selectedTemplateData) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(59,130,246,0.05)", borderRadius: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedClientData && (
                    <Chip
                      icon={<BusinessIcon />}
                      label={`Client: ${selectedClientData.name}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {selectedEngineerData && (
                    <Chip
                      icon={<EngineeringIcon />}
                      label={`Engineer: ${selectedEngineerData.firstName} ${selectedEngineerData.lastName}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {selectedTemplateData && (
                    <Chip
                      icon={<DescriptionIcon />}
                      label={`Template: ${selectedTemplateData.name}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Section: Site Details */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <LocationOnIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Site Details
              </Typography>
              <Chip size="small" label="Auto-filled from client" color="info" variant="outlined" />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Address"
                  value={siteAddress}
                  onChange={(e) => {
                    setSiteAddress(e.target.value);
                    if (errors.siteAddress) setErrors({ ...errors, siteAddress: null });
                  }}
                  fullWidth
                  required
                  error={!!errors.siteAddress}
                  helperText={errors.siteAddress || "Where the job will take place"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Contact"
                  value={siteContact}
                  onChange={(e) => {
                    setSiteContact(e.target.value);
                    if (errors.siteContact) setErrors({ ...errors, siteContact: null });
                  }}
                  fullWidth
                  required
                  error={!!errors.siteContact}
                  helperText={errors.siteContact || "Phone or email for on-site contact"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
{/* Section: End Customer Details (Optional - For Resellers) */}
          <Box>
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1, 
                mb: 2,
                cursor: "pointer",
                "&:hover": { opacity: 0.8 }
              }}
              onClick={() => setShowEndCustomer(!showEndCustomer)}
            >
              <PersonAddIcon color="secondary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                End Customer Details (Optional)
              </Typography>
              <Chip 
                size="small" 
                label="For Reseller Jobs" 
                color="secondary" 
                variant="outlined" 
              />
              <IconButton size="small">
                {showEndCustomer ? <ClearIcon /> : <AddCircleIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={showEndCustomer}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>For reseller jobs:</strong> When a reseller orders installation for their customer, 
                  fill in the actual installation location below. The site address above will auto-fill with 
                  the reseller's address, but the job sheet will use these end customer details instead.
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Customer Name"
                    value={endCustomerName}
                    onChange={(e) => setEndCustomerName(e.target.value)}
                    fullWidth
                    helperText="Company receiving the installation"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Customer Address"
                    value={endCustomerAddress}
                    onChange={(e) => setEndCustomerAddress(e.target.value)}
                    fullWidth
                    multiline
                    rows={1}
                    helperText="Full address where installation will take place"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Customer Contact"
                    value={endCustomerContact}
                    onChange={(e) => setEndCustomerContact(e.target.value)}
                    fullWidth
                    helperText="Contact person at installation site"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonAddIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Customer Phone"
                    value={endCustomerPhone}
                    onChange={(e) => setEndCustomerPhone(e.target.value)}
                    fullWidth
                    helperText="Phone number at installation site"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  📋 <strong>Result:</strong><br />
                  • Invoice will be addressed to: <strong>{selectedClient ? clients.find(c => c.id === selectedClient)?.name || "selected customer" : "selected customer"}</strong> (who pays)<br />
                  • Job sheet will show: <strong>{endCustomerName || "end customer"}</strong> at <strong>{endCustomerAddress || "end customer address"}</strong><br />
                  • Engineer will go to: <strong>{endCustomerAddress || "the end customer address"}</strong>
                </Typography>
              </Box>
            </Collapse>
          </Box>
          <Divider />

          {/* Section: Vehicle & Notes */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <DirectionsCarIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Vehicle & Notes
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Registration"
                  value={vehicleReg}
                  onChange={(e) => {
                    setVehicleReg(e.target.value.toUpperCase());
                    if (errors.vehicleReg) setErrors({ ...errors, vehicleReg: null });
                  }}
                  fullWidth
                  required
                  error={!!errors.vehicleReg}
                  helperText={errors.vehicleReg}
                  placeholder="e.g., AB12 CDE"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DirectionsCarIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Make/Model"
                  value={vehicleMake}
                  onChange={(e) => setVehicleMake(e.target.value)}
                  fullWidth
                  placeholder="e.g., Ford Transit"
                  helperText="Optional"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DirectionsCarIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Job Notes (for Engineer)"
                  value={jobNotes}
                  onChange={(e) => setJobNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Any special instructions or information for the engineer..."
                  helperText="Optional - visible to the assigned engineer"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <NotesIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Submit & Actions */}
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading || clients.length === 0 || engineers.length === 0 || templates.length === 0}
                startIcon={isLoading ? null : <CheckCircleIcon />}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Create & Assign Job"}
              </Button>

              <Tooltip title="Clear all fields">
                <Button
                  variant="outlined"
                  onClick={handleClearForm}
                  disabled={isLoading}
                  startIcon={<ClearIcon />}
                  sx={{ minWidth: { sm: 150 } }}
                >
                  Clear Form
                </Button>
              </Tooltip>

              {lastCreatedJobNumber && (
                <Tooltip title="Keep selections for another job">
                  <Button
                    variant="outlined"
                    onClick={handleDuplicateLast}
                    disabled={isLoading}
                    startIcon={<ContentCopyIcon />}
                    sx={{ minWidth: { sm: 150 } }}
                  >
                    Duplicate
                  </Button>
                </Tooltip>
              )}
            </Stack>

            {/* Success/Error Message */}
            <Collapse in={!!message} sx={{ mt: 2 }}>
              {message && (
                <Alert
                  severity={message.type}
                  sx={{
                    animation: 'fadeIn 0.3s ease-in',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(-10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  {message.text}
                </Alert>
              )}
            </Collapse>

            {lastCreatedJobNumber && (
              <Alert severity="success" icon={<InfoIcon />} sx={{ mt: 2 }}>
                Last created job: <strong>#{lastCreatedJobNumber}</strong>
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateJob;